import { motion } from 'framer-motion';
import React from 'react';
import styled from 'styled-components';

import { theme } from '../../theme';

interface TabItem {
  id: string | number;
  label: string;
  icon?: React.ReactNode;
}

interface TabSelectorProps {
  items: TabItem[];
  activeId: string | number;
  onTabChange: (id: string | number) => void;
  color?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
}

const TabContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
`;

const TabButton = styled(motion.button)<{
  $isActive: boolean;
  $primaryColor: string;
  $secondaryColor: string;
  $tertiaryColor: string;
}>`
  background-color: ${props =>
    props.$isActive ? `rgba(${props.$primaryColor}, 0.2)` : 'rgba(0, 20, 0, 0.2)'};
  color: ${props => (props.$isActive ? theme.colors.green : props.$tertiaryColor)};
  border: 1px solid ${props => (props.$isActive ? theme.colors.green : props.$tertiaryColor)};
  border-radius: 4px;
  padding: 8px 12px;
  font-family: 'Courier New', monospace;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;

  svg {
    margin-right: 8px;
    color: ${props => (props.$isActive ? theme.colors.green : props.$tertiaryColor)};
  }

  &:hover {
    background-color: rgba(${props => props.$primaryColor}, 0.1);
    color: ${theme.colors.green};
    border-color: ${theme.colors.green};

    svg {
      color: ${theme.colors.green};
    }
  }
`;

// Helper function to convert hex to RGB
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '31, 207, 94'; // Default to green if conversion fails
};

const TabSelector: React.FC<TabSelectorProps> = ({
  items,
  activeId,
  onTabChange,
  color = theme.colors.green,
  secondaryColor = '#e6c84c',
  tertiaryColor = '#a0a0a0',
}) => {
  const primaryColorRgb = hexToRgb(color);

  return (
    <TabContainer>
      {items.map(item => (
        <TabButton
          key={item.id}
          $isActive={activeId === item.id}
          $primaryColor={primaryColorRgb}
          $secondaryColor={secondaryColor}
          $tertiaryColor={tertiaryColor}
          onClick={() => onTabChange(item.id)}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 25,
          }}
        >
          {item.icon} {item.label}
        </TabButton>
      ))}
    </TabContainer>
  );
};

export default TabSelector;
